<template>
  <NuxtLayout v-if="cottageData.success" name="standard-aside">
    <ConnectifTag :name="CONNECTIF_PAGE_TAGS.DETAIL" />
    <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.TRANSACTIONAL" />
    <ConnectifCategory
      v-if="cottageData.success"
      :name="cottageData.success.data.province.name"
    />
    <template #aside-desktop>
      <ClientOnly>
        <template #fallback>
          <CottagePricesAsideSkeleton
            class="p-3 sm:p-5 grid grid-cols-6 gap-3 fixed left-0 right-0 bottom-0 z-50 lg:z-0 lg:relative lg:left-auto lg:right-auto lg:bottom-auto bg-white ring-1 rounded-lg ring-gray-200"
          />
        </template>
        <LazyCottagePricesAside
          v-if="pricesData.getCottageDetail"
          v-model:open-date-modal="isOpenDateModal"
          v-model:submit-form="isSubmitPricesFormClicked"
          v-model:open-capacity-input="isOpenCapacityInput"
          :disable-see-prices-button="!canSeePrices"
          :is-online-booking="
            pricesData.getCottageDetail.salesService.isOnlineBookingActive
          "
          @blur:capacity="isOpenCapacityInput = false"
          @enable:see-price-button="canSeePrices = true"
          @submit:prices="submitPrices"
          @submited="resetSubmitButton"
          @show:price-details="openSummaryModal()"
        />
      </ClientOnly>
    </template>

    <UStepper
      v-model="activeItemStepper"
      disabled
      :items="itemsStepper"
      class="w-full mt-8"
    >
      <template #prices>
        <div v-if="pricesData.getCottageDetail">
          <div class="mb-5">
            <TTitle tag="h1" size="2xl" classes="mb-2">
              {{ t('views.cottage-prices.rentUnitsAndPrices') }}
            </TTitle>
            <TText classes="mb-2">
              {{ pricesData.getCottageDetail?.name }}
            </TText>

            <ULink
              class="text-sm"
              :active="true"
              :to="
                routes().cottageDetail({
                  slug: pricesData.getCottageDetail?.slug,
                  province: pricesData.getCottageDetail?.province.slug,
                })
              "
              external
              >{{ t('views.cottage-prices.cottageLink') }}</ULink
            >
          </div>

          <UAlert
            v-if="hasError"
            :title="t('views.cottage-prices.error')"
            color="error"
            variant="soft"
            class="mb-4"
            icon="i-heroicons-x-circle"
          />

          <LazyCottagePricesNoVacancyAlert
            v-if="hasNoVacancy"
            :slug="pricesData.getCottageDetail?.village.slug"
            @button-click="isOpenCapacityInput = true"
          />

          <LazyCottagePricesSummary
            v-if="hasPrices"
            :date-in="(route.query.dateIn as string) || ''"
            :date-out="(route.query.dateOut as string) || ''"
            :capacity="(route.query.capacity as string) || ''"
            :nights="pricesData.nights"
            class="mb-5"
            @open-date-modal="pricesData.clearRentUnitSelections()"
          />

          <LazyCottagePricesRentUnits
            v-if="pricesData.rentUnitsDetail"
            :disable-rent-units="hasError || hasNoVacancy"
            @open-modal="openModal"
            @open-date-modal="openDateModal"
            @submit-prices-form="submitPricesForm"
          />
        </div>

        <LazyCottagePricesOwnerDetail v-if="pricesData.cottageOwnerDetail" />
        <LazyCottagePricesConditions
          v-if="pricesData.cottageConditions?.cottageConditions"
          :data="pricesData.cottageConditions.cottageConditions"
          @open-date-modal="openDateModal"
        />
        <LazyCottagePricesRules
          v-if="pricesData.cottageConditions?.cottageConditions"
          :data="pricesData.cottageConditions.cottageConditions"
        />
      </template>

      <template #extras>
        <template
          v-if="
            pricesData.rentUnitsPrices !== null &&
            pricesData.getCottageDetail &&
            pricesData.hasExtrasAvailable &&
            activeItemStepper === 1
          "
        >
          <TTitle tag="h1" size="2xl" classes="mb-2">
            {{ t('views.cottage-prices.extras.title') }}
          </TTitle>

          <TText classes="mb-2">
            {{ pricesData.getCottageDetail?.name }}
          </TText>

          <ULink
            class="text-sm mb-8 block"
            :active="true"
            :to="
              routes().cottageDetail({
                slug: pricesData.getCottageDetail?.slug,
                province: pricesData.getCottageDetail?.province.slug,
              })
            "
            external
            >{{ t('views.cottage-prices.cottageLink') }}</ULink
          >

          <LazyCottagePricesExtras
            v-for="rentUnit in pricesData.getSelectedRentUnitsWithExtrasAvailable"
            :key="rentUnit.id"
            :rent-unit-id="rentUnit.id"
            :nights="pricesData.nights"
            @open-modal="openModal"
          />
        </template>
      </template>
    </UStepper>

    <LazyCottagePricesRentUnitModal
      v-model="modalIsOpen"
      :rent-unit="selectedRentUnit"
    />
    <LazyCottagePricesSummaryModal v-model="summaryModalOpened" />
    <LazyAuthModal v-model="authModalIsOpen">
      <template #default>
        <CottagePricesOnlineBookingRedirect @submit:prices="submitPrices" />
      </template>
    </LazyAuthModal>
    <SSpacer />
    <SSpacer />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { datadogLogs } from '@datadog/browser-logs'
import type { StepperItem } from '@nuxt/ui'
import { ALIAS_PATH } from '~~/modules/views/cottage-prices/constants'
import type { schemaType as rentUnitStructureSchemaType } from '~~/shared/api/rurality/v3/cottages/rent-units-structure/schema'
import { usePageConfiguration } from '~~/modules/views/cottage-prices/composables/usePageConfiguration'
import { usePageRedirects } from '~~/modules/views/cottage-prices/composables/usePageRedirects'
import { sendConnectifPricePageProduct } from '~~/modules/integrations/connectif/composables/useConnectifComposable'
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendGa4PricesGoBack } from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { CONNECTIF_DEFAULT_PRICE } from '~~/modules/integrations/connectif/constants/index'
import { serverFetch as fetchRuralityOwnersDetail } from '~~/modules/api/rurality/v3/owners/detail'
import { serverFetch as fetchRuralityCottagesDetail } from '~~/modules/api/rurality/v3/cottages/detail'
import { serverFetch as fetchRuralityRentUnits } from '~~/modules/api/rurality/v3/cottages/rent-units-detail'
import type { schemaType as rentUnitsSchemaType } from '~~/shared/api/rurality/v3/cottages/rent-units-detail/schema'
import { browserFetch as fetchRuralityCottageConditions } from '~~/modules/api/rurality/v3/cottages/conditions'
import { serverFetch as fetchRuralityRentUnitsPrice } from '~~/modules/api/rurality/v3/cottages/rent-units-price'
import { serverFetch as fetchRuralityRentUnitStructure } from '~~/modules/api/rurality/v3/cottages/rent-units-structure'
import { serverFetch as fetchRuralityStatsCottage } from '~~/modules/api/rurality/v3/stats/cottage/new'
import { serverFetch as fetchRuralityOnlineBookingCreate } from '~~/modules/api/rurality/v3/online-bookings/create'
import { usePricesStore } from '~~/modules/views/cottage-prices/stores/pricesStore'
import { useUserStore } from '~~/modules/auth/stores/userStore'

/* COMPOSABLES */
const route = useRoute()
const { showErrorPage, redirectDeletedPage } = usePageRedirects()
const pricesData = usePricesStore()
const userStore = useUserStore()

/* SIMPLE REACTIVE STATE - NO STATE MACHINE! */
const hasError = ref(false)
const hasNoVacancy = ref(false)
const canSeePrices = ref(false)

/* CONSTANTS */
const activeItemStepper = ref(0)
const itemsStepper: Ref<StepperItem[]> = ref([
  {
    slot: 'prices' as const,
    title: t('views.cottage-prices.stepper.prices'),
    icon: 'i-heroicons-home',
  },
  {
    slot: 'prebooking' as const,
    title: t('views.cottage-prices.stepper.prebooking'),
    icon: 'i-heroicons-rocket-launch',
  },
] satisfies StepperItem[])

const itemsStepperWithExtras = [
  {
    slot: 'prices' as const,
    title: t('views.cottage-prices.stepper.prices'),
    icon: 'i-heroicons-home',
  },
  {
    slot: 'extras' as const,
    title: t('views.cottage-prices.stepper.extras'),
    icon: 'i-heroicons-star',
  },
  {
    slot: 'prebooking' as const,
    title: t('views.cottage-prices.stepper.prebooking'),
    icon: 'i-heroicons-rocket-launch',
  },
] satisfies StepperItem[]

const slug = route.params.slug as string
const province = route.params.province as string
const rentUnitsStructureData: Ref<rentUnitStructureSchemaType | null> =
  ref(null)

definePageMeta({
  alias: ALIAS_PATH,
})

/* REFS */
const modalIsOpen = ref<boolean>(false)
const authModalIsOpen = ref(false)
const selectedRentUnit = ref<any>(null)
const isOpenDateModal = ref(false)
const summaryModalOpened = ref(false)
const isSubmitPricesFormClicked = ref(false)
const isOpenCapacityInput = ref(false)

/* COMPUTED */
const hasPrices = computed(() => {
  return (
    !!pricesData.rentUnitsPrices &&
    pricesData.dateIn &&
    pricesData.dateOut &&
    pricesData.numPeople
  )
})

/* FUNCTIONS */
const openModal = (rentUnit: rentUnitsSchemaType['rentUnits'][number]) => {
  modalIsOpen.value = true
  selectedRentUnit.value = rentUnit
}

const openDateModal = async () => {
  isOpenDateModal.value = true
  pricesData.clearRentUnitSelections()
}

const openSummaryModal = () => {
  summaryModalOpened.value = true
}

const submitPricesForm = () => {
  isSubmitPricesFormClicked.value = true
}

const resetSubmitButton = () => {
  isSubmitPricesFormClicked.value = false
}

const submitPrices = async () => {
  authModalIsOpen.value = false

  if (pricesData.getCottageDetail && pricesData.rentUnitsPrices) {
    const hasExtrasAvailable =
      pricesData.hasSelectedRentUnitsWithExtrasAvailable

    if (activeItemStepper.value === 0 && hasExtrasAvailable) {
      history.pushState(history.state, '', `${route.fullPath}#extras`)
      activeItemStepper.value = 1
    } else {
      pricesData.setPricesData()

      if (pricesData.getCottageDetail.salesService.isOnlineBookingActive) {
        const user = userStore.getUserdata.value

        if (!user.logged) {
          authModalIsOpen.value = true
        } else {
          const onlineBookingCreate = await fetchRuralityOnlineBookingCreate({
            ...pricesData.getOnlineBookingData,
          })

          if (onlineBookingCreate.success && onlineBookingCreate.success.data) {
            navigateTo(
              routes().cottageContactOnlineBooking({
                province: route.params.province as string,
                slug: route.params.slug as string,
                id: onlineBookingCreate.success.data.onlineBookingId,
              }),
            )
          }
        }
      } else {
        datadogLogs.logger.info(
          'NuxtFunnelLogs: Step 4 - Go to PreBooking Page',
        )
        navigateTo(
          routes().cottageContactPrebooking({
            slug: route.params.slug as string,
            province: route.params.province as string,
          }),
        )
      }
    }
  }
}

function handlePopStateExtras() {
  activeItemStepper.value = 0
  pricesData.clearRentUnitSelections()
  sendGa4PricesGoBack()
}

// SSR: Fetch cottage data and preload related data in parallel
const cottageData = await fetchRuralityCottagesDetail({
  slug,
  province,
})

if (cottageData.success) {
  usePageConfiguration(cottageData.success.data)

  sendConnectifPricePageProduct({
    ...cottageData.success.data,
    productPrice: CONNECTIF_DEFAULT_PRICE.CONTACT,
  })

  // SSR: Prefill store to avoid client-side refetch
  pricesData.setCottageDetail(cottageData.success.data)
  pricesData.setInitialData({
    dateIn: route.query.dateIn as string,
    dateOut: route.query.dateOut as string,
    numPeople: parseInt(route.query.capacity as string) || 0,
  })

  // SSR: Parallel prefetch of all related data
  const [ownerData, rentUnitsData, rentUnitsStructure, pricesDataResult] =
    await Promise.all([
      fetchRuralityOwnersDetail({
        id: cottageData.success.data.owner.id,
      }),
      fetchRuralityRentUnits({
        id: cottageData.success.data.id,
      }),
      fetchRuralityRentUnitStructure({
        cottageId: cottageData.success.data.id,
      }),
      // Only fetch prices if query params exist
      route.query.dateIn && route.query.dateOut && route.query.capacity
        ? fetchRuralityRentUnitsPrice({
            cottageId: cottageData.success.data.id,
            dateIn: toRuralityDate().apiDate(route.query.dateIn as string),
            dateOut: toRuralityDate().apiDate(route.query.dateOut as string),
            numPeople: parseInt(route.query.capacity as string),
          })
        : Promise.resolve(null),
    ])

  const mandatoryDataError = rentUnitsData.error || rentUnitsStructure.error

  if (mandatoryDataError) {
    showErrorPage(mandatoryDataError as ApiResponseError)
  }

  // Populate store with prefetched data
  if (ownerData.success) {
    pricesData.setCottageOwnerDetail(ownerData.success.data)
  }

  if (rentUnitsData.success) {
    pricesData.setRentUnitsDetail(rentUnitsData.success.data)
  }

  if (rentUnitsStructure.success) {
    rentUnitsStructureData.value = rentUnitsStructure.success.data
    pricesData.setRentUnitsStructure(rentUnitsStructure.success.data)
  }

  if (pricesData.hasExtrasAvailable === true) {
    itemsStepper.value = itemsStepperWithExtras
  }

  // Handle prices response
  if (pricesDataResult) {
    if ('success' in pricesDataResult && pricesDataResult.success) {
      pricesData.setRentUnitsPrices(pricesDataResult.success.data)

      await fetchRuralityStatsCottage({
        cottageId: cottageData.success.data.id,
        eventType: 'rentunit_search',
      })
    } else if ('error' in pricesDataResult && pricesDataResult.error) {
      // Handle error responses
      if (pricesDataResult.error.statusCode === 416) {
        hasNoVacancy.value = true
      } else {
        hasError.value = true
      }
    }
  }

  // Mark as ready - no more loading
  canSeePrices.value = !route.query.dateIn || !route.query.dateOut

  // Log when content is loaded (SSR or client)
  if (import.meta.client) {
    datadogLogs.logger.info('NuxtFunnelLogs: Step 2 - Loading Prices Page')
  }
} else if (cottageData.error) {
  if (cottageData.error.statusCode === 412) {
    await redirectDeletedPage(province)
  } else {
    showErrorPage(cottageData.error as ApiResponseError)
  }
}

onMounted(async () => {
  if (import.meta.client) {
    window.addEventListener('popstate', handlePopStateExtras)
    datadogLogs.logger.info('NuxtFunnelLogs: Step 3 - Loaded Prices Page')

    if (route.query.rent_unit) {
      // Auto-select rent unit from deal
      const rentUnitId = String(route.query.rent_unit)
      const capacity = parseInt(route.query.capacity as string) || 1
      if (pricesData.canAutoSelect(rentUnitId)) {
        pricesData.toggleRentUnit(rentUnitId, capacity)
      }
    } else if (!pricesData.hasSelectedRentUnits) {
      pricesData.autoSelectSingleUnit()
    }
  }
  if (cottageData.success) {
    const options = {
      id: cottageData.success.data.id,
    }
    if (route.query.dateIn) {
      Object.assign(options, {
        dateIn: toRuralityDate().apiDate(route.query.dateIn as string),
      })
    }
    const cottageConditionsData = await fetchRuralityCottageConditions(options)

    if (cottageConditionsData.success) {
      pricesData.setCottageConditions(cottageConditionsData.success.data)
    }
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('popstate', handlePopStateExtras)
  }
})
</script>
